import { Link } from 'react-router-dom';
import styled from 'styled-components';
import BlankImage from '../../../../assets/images/blank-image.png';
import { Icon } from '../../../../components/common/icon/Icon';
import { IInformation } from '../../../../interfaces/Information.interfaces';
import { formatDate } from '../../../../utils/formatter/formatDate.util';

interface InfoVerticalListViewProps {
  information: IInformation;
}

export const InfoVerticalListView = ({ information }: InfoVerticalListViewProps) => {
  return (
    <Container to={`/info/${information.id}`}>
      <ThumbnailImageWrapper>
        <ThumbnailImage src={information.thumbnailUrl ?? BlankImage} loading={'lazy'} />
      </ThumbnailImageWrapper>

      <InfoSection>
        <HeaderContainer>
          <HeaderLeftSection>
            {information.category === 'NEWS' && <NewsCategory>{'뉴스'}</NewsCategory>}
            {information.category === 'MAGAZINE' && <MagazineCategory>{'매거진'}</MagazineCategory>}
            <SplitBar>{'|'}</SplitBar>
            <CreateDate>{formatDate(information.createdAt)}</CreateDate>
          </HeaderLeftSection>

          <HeaderRightSection>
            <Icon name={'Eye'} width={14} height={14} fill={'#858894'} />
            <Views>{information.views.toLocaleString()}</Views>
          </HeaderRightSection>
        </HeaderContainer>

        <TextSection>
          <Title>{information.title}</Title>
          <PreviewContent>{information.previewContent}</PreviewContent>
        </TextSection>
      </InfoSection>
    </Container>
  );
};

const Container = styled(Link)`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  cursor: pointer;
`;

const ThumbnailImageWrapper = styled.div`
  width: 100%;
  padding-top: 75%;

  position: relative;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;

  position: absolute;

  left: 0;
  top: 0;

  object-fit: cover;

  border-radius: var(--radius-none, 0px);

  border-radius: 8px 8px 0px 0px;
`;

const InfoSection = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: 16px;

  padding: 24px;
`;

const HeaderContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeftSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 4px;
`;

const HeaderRightSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  gap: 2px;
`;

const Category = styled.span`
  text-align: justify;

  /* System/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 11.9px */
`;

const NewsCategory = styled(Category)`
  color: var(--light-scheme-color-primary-blue, #196eff);
`;

const MagazineCategory = styled(Category)`
  color: var(--light-scheme-color-primary-red, #ff3049);
`;

const SplitBar = styled.span`
  color: var(--Light-Scheme-Monochrome-grey50, #babdca);
  text-align: justify;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const CreateDate = styled.div`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: justify;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const Views = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const Title = styled.h2`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-overflow: ellipsis;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const PreviewContent = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  align-self: stretch;

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: justify;
  text-overflow: ellipsis;

  /* Display/Large/l-light */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 130%; /* 20.8px */
`;

const TextSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  gap: 4px;
`;
